import { HttpClient } from '@wix/http-client';
import type {
  AppData,
  AppDataComponent,
  DocumentServicesObject,
} from '@wix/document-services-types';
import { CompRef } from '@wix/document-services-types';
import type {
  AppInstallOption,
  PlatformApiMethods,
  PluginInstallationParams,
} from '@wix/editor-platform-host-integration-apis';
import type { PluginPlacement } from '@wix/ambassador-devcenter-appsruntimereader-v1-app-runtime-data/types';
import {
  ComponentType,
  WidgetPluginComponentData,
} from '@wix/ambassador-app-service-webapp/types';
import { SlotData, WidgetPointer } from '../../types/widgetPlugins';
import { findIndex } from 'lodash';
import { createPluginHostApi } from './plugins/pluginHostAPI';
import {
  canOpenModal as canOpenReplacePluginModal,
  type ReplacePluginModalProps,
  type Plugin,
  type ReplacementOption,
} from '@wix/editor-platform-shared-ui';
import { IExperimentsApiAdapter } from '../utils/experimentsApiAdapter';
import { SlotPointer } from './plugins/slotPointer';

// TODO: move methods to SlotsService and PluginService
interface SlotsResponse {
  slots: SlotData[];
}

const getSlotComponentRef = (
  documentServicesAPI: DocumentServicesObject,
  slot: SlotData,
): CompRef | null => {
  const VIRTUAL_SLOT_ID_DIVIDER = '_vs_';
  const isBlocksSlot = slot?.scope?.componentIds?.length;

  if (isBlocksSlot) {
    const [hostWidgetId] = slot!.scope!.componentIds!;
    const hostWidgetCompRef =
      documentServicesAPI.components.get.byId(hostWidgetId);

    if (!hostWidgetCompRef) {
      return null;
    }

    const widgetCompSlots =
      documentServicesAPI.components.slots.getWidgetSlots(hostWidgetCompRef);

    if (!widgetCompSlots) {
      return null;
    }

    const slotComponent = widgetCompSlots?.find(
      ({ role }) => role === slot.slotId,
    );

    if (!slotComponent) {
      return null;
    }

    return slotComponent.compRef as CompRef;
  } else {
    const widgetCompRef = documentServicesAPI.components.get.byId(
      slot.componentId,
    );

    if (!widgetCompRef) {
      return null;
    }

    return {
      id: `${widgetCompRef.id}${VIRTUAL_SLOT_ID_DIVIDER}${slot.slotId}`,
      type: widgetCompRef.type,
    };
  }
};

const getAvailableSlots = async (
  documentServicesAPI: DocumentServicesObject,
  pluginPlacements: PluginPlacement[] = [],
): Promise<SlotData[]> => {
  const META_SITE_APPLICATION_ID = '-666';
  const getAppToken = () =>
    documentServicesAPI.platform.getAppDataByApplicationId(
      META_SITE_APPLICATION_ID,
    )?.instance;

  const httpClient = new HttpClient({
    baseURL: '/_serverless/editor-platform-widget-plugins/',
    getAppToken,
  });

  if (!pluginPlacements?.length) {
    return [];
  }

  const queryParams = new URLSearchParams();
  queryParams.append('placements', JSON.stringify(pluginPlacements));
  queryParams.append('withSlotContent', 'true');
  const res = await httpClient.get<SlotsResponse>(
    `/slots?${queryParams.toString()}`,
  );

  return res.data.slots;
};

function sortSlotsByPluginPlacements(
  slots: SlotData[],
  placements: PluginPlacement[],
) {
  const sortedSlots = [...slots].sort(function (a, b) {
    return (
      findIndex(placements, { widgetId: a.widgetId, slotId: a.slotId }) -
      findIndex(placements, { widgetId: b.widgetId, slotId: b.slotId })
    );
  });
  return sortedSlots;
}

type WidgetPluginComponentDataWithComponentId = WidgetPluginComponentData & {
  componentId: string;
};

const getWidgetPluginsComponentData = (
  components: AppDataComponent[],
): WidgetPluginComponentDataWithComponentId[] => {
  const isPluginCompType = (comp: AppDataComponent) =>
    comp.type === ComponentType.WIDGET_PLUGIN;

  // WidgetPluginComponentData has an explicit type as AppDataComponent['data'] doesn't provide a correct type for all possible component types
  // @ts-expect-error
  return (
    components?.filter(isPluginCompType).map((widget) => ({
      ...widget.data,
      componentId: widget.componentId,
    })) ?? []
  );
};

const confirmPluginReplace = async (
  plugins: Plugin[],
  slots: SlotData[],
  platformAPI: PlatformApiMethods,
): Promise<ReplacementOption[]> => {
  const panelName = 'tpaPanels.confirmation.pluginReplace';
  const languageCode = window.editorModel?.languageCode;

  const closePanel = () => {
    platformAPI.editor.closePanel({} as AppData, panelName);
  };

  return new Promise((resolve) => {
    const onCancel = () => {
      closePanel();
      resolve([]);
    };

    const onReplace = (replacementOptions: ReplacementOption[]) => {
      closePanel();
      resolve(replacementOptions);
    };

    const onHelp = () => {
      const helpArticleId = '68ab8d2f-c2d2-4601-95d0-52d25ba1c24d';
      const url = new URL(
        `https://wix.wixanswers.com/apps/widget/v1/wix/53f85d67-f8b0-41ca-9dc8-5a16390cc74f/view/${languageCode}/article/${helpArticleId}`,
      );

      window.open(url, '_blank');
    };

    platformAPI.editor.openPanel(
      {} as AppData,
      panelName,
      {
        width: '720px',
        token: panelName,
        plugins,
        slots,
        onCancel,
        onReplace,
        onHelp,
        onClose: onCancel,
        cancelActionType: 'skip',
        origin: 'installation',
        locale: languageCode,
      },
      true,
    );
  });
};

type SlotDataWithWidgetName = SlotData & { widgetName: string };

const getAllPluginsInstallationParams = async (
  documentServicesAPI: DocumentServicesObject,
  appDefinitionId: string,
  components: AppDataComponent[],
  platformAPI: PlatformApiMethods,
  experimentsAPI: IExperimentsApiAdapter,
  appsOptions?: AppInstallOption,
): Promise<PluginInstallationParams[]> => {
  const widgetPlugins = getWidgetPluginsComponentData(components).filter(
    (plugin) => plugin.installation?.base?.autoAdd,
  );

  if (widgetPlugins?.length === 0) {
    return [];
  }

  const pluginInstallationParams: PluginInstallationParams[] = [];

  const pluginsNeedingReplacement: Plugin[] = [];
  const replacementSlots: SlotDataWithWidgetName[] = [];

  const getPluginInstallationParam = (
    slot: SlotData,
    widgetRef: WidgetPointer,
  ) => {
    return {
      slotComponentRef: getSlotComponentRef(documentServicesAPI, slot)!,
      widgetRef,
      pageId: slot.pageId,
    };
  };

  const findFirstFreeSlot = (slots: SlotData[]): SlotData | undefined => {
    return slots.find((slot) => {
      const slotComponentRef = getSlotComponentRef(documentServicesAPI, slot);

      if (!slotComponentRef) {
        return false;
      }
      const api = createPluginHostApi(documentServicesAPI, experimentsAPI);

      try {
        const slotData = api.getWidgetSlot(
          new SlotPointer({ slotRef: slotComponentRef }),
          { includePrivate: false },
        );

        return !slotData?.pluginInfo;
      } catch {
        return false;
      }
    });
  };

  for (const plugin of widgetPlugins) {
    const availableSlots = await getAvailableSlots(
      documentServicesAPI,
      plugin.placements,
    );

    if (!availableSlots.length) {
      continue;
    }

    replacementSlots.push(
      ...availableSlots.map((slot) => ({
        ...slot,
        widgetName: slot.pageTitle,
      })),
    );

    const slots = sortSlotsByPluginPlacements(
      availableSlots,
      plugin.placements || [],
    );

    const firstFreeSlot = findFirstFreeSlot(slots);

    if (!firstFreeSlot) {
      pluginsNeedingReplacement.push({
        id: plugin.componentId,
        name: plugin.marketData?.name!,
        icon: plugin.marketData?.logoUrl,
        // @ts-expect-error
        placements: plugin.placements!,
      });

      continue;
    }

    pluginInstallationParams.push(
      getPluginInstallationParam(firstFreeSlot, {
        appDefinitionId,
        widgetId: plugin.referenceComponentId!,
      }),
    );
  }

  const shouldOpenReplacePluginModal = [
    canOpenReplacePluginModal(
      pluginsNeedingReplacement as ReplacePluginModalProps['plugins'],
      replacementSlots as ReplacePluginModalProps['slots'],
    ),
    appsOptions?.pluginsAutoPopulate?.isSilent !== true,
  ].every(Boolean);

  if (!shouldOpenReplacePluginModal) {
    return pluginInstallationParams;
  }

  const replacements = await confirmPluginReplace(
    pluginsNeedingReplacement,
    replacementSlots,
    platformAPI,
  );

  replacements.forEach((replacement) => {
    const slot = replacementSlots.find(
      (findSlot) =>
        findSlot.slotId === replacement.placement.slotId &&
        findSlot.widgetId === replacement.placement.widgetId,
    )!;

    const plugin = widgetPlugins.find(
      (findPlugin) => findPlugin.componentId === replacement.pluginId,
    )!;

    pluginInstallationParams.push(
      getPluginInstallationParam(slot, {
        appDefinitionId,
        widgetId: plugin.referenceComponentId!,
      }),
    );
  });

  return pluginInstallationParams;
};

const getPluginInstallationParams = async (
  documentServicesAPI: DocumentServicesObject,
  appDefinitionId: string,
  components: AppDataComponent[],
): Promise<PluginInstallationParams | null> => {
  const widgetPlugins = getWidgetPluginsComponentData(components);

  if (widgetPlugins?.length === 0) {
    return null;
  }

  for (const plugin of widgetPlugins) {
    if (!plugin.installation?.base?.autoAdd) {
      continue;
    }

    const availableSlots = await getAvailableSlots(
      documentServicesAPI,
      plugin.placements,
    );

    if (!availableSlots.length) {
      continue;
    }

    const [slot] = sortSlotsByPluginPlacements(
      availableSlots,
      plugin.placements || [],
    );
    const slotComponentRef = getSlotComponentRef(documentServicesAPI, slot);

    if (!slotComponentRef) {
      return null;
    }

    const widgetRef: WidgetPointer = {
      appDefinitionId,
      widgetId: plugin.referenceComponentId!,
    };

    return {
      slotComponentRef,
      widgetRef,
      pageId: slot.pageId,
    };
  }

  return null;
};

export { getPluginInstallationParams, getAllPluginsInstallationParams };
